import React from 'react';
import styled from 'styled-components';
import { Button as AntdButton } from 'antd';
import { useBreakpoint } from 'hooks';
import { ButtonProps as AntdButtonProps } from 'antd/lib/button';
import { Color } from '@bb/elements-web';
import { featureEnabled } from 'utils';
import Icon from '../Icon';
import Spin from '../Spin';
import theme from '../theme';

export interface ButtonProps extends AntdButtonProps {
  strong?: boolean;
  noBorder?: boolean;
  underlineOnHover?: boolean;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      strong,
      disabled,
      type = 'default',
      noBorder,
      underlineOnHover,
      href,
      target,
      ...props
    },
    ref,
  ) => {
    const { md } = useBreakpoint();
    const button = (
      <StyledButton
        {...props}
        $noBorder={noBorder}
        ref={ref}
        $strong={strong}
        $underlineOnHover={underlineOnHover}
        type={type}
        disabled={disabled}
        $md={md}
        $hasSupraRebrandingFeature={featureEnabled('supraRebrandingFeature', 1)}
      />
    );
    if (href) {
      return (
        <StyledLink disabled={disabled} href={href} target={target}>
          {button}
        </StyledLink>
      );
    }
    return button;
  },
);

const StyledButton = styled(AntdButton)<{
  $noBorder?: boolean;
  $strong?: boolean;
  $underlineOnHover?: boolean;
  type: string;
  danger?: AntdButtonProps['danger'];
  disabled?: boolean;
  $md: boolean;
  $hasSupraRebrandingFeature?: boolean;
}>`
  && {
    ${({ $strong }) =>
      $strong ? `font-weight: bold` : `font-weight: ${theme.btnFontWeight}`};
    ${({ $underlineOnHover }) =>
      $underlineOnHover &&
      `
      &:hover > span {
        text-decoration: underline;
      }
      `}
    ${({ $noBorder }) =>
      $noBorder &&
      `
      border: 0;
      box-shadow: none;
      &::after {
        display: none;
      }
    `};
    ${({ type, $hasSupraRebrandingFeature, disabled, danger }) =>
      $hasSupraRebrandingFeature &&
      type === 'primary' &&
      !danger &&
      !disabled &&
      `background-color: ${Color.PRIMARY}; border-color: ${Color.PRIMARY};`}
    ${({ type, danger, disabled, $md }) =>
      type === 'default' &&
      !danger &&
      !disabled &&
      $md &&
      `
      &:hover, &:focus {
        ${Icon} svg {
          fill: ${theme.getColor('primary')};
          color: ${theme.getColor('primary')};
        }
      }
      ${Icon} svg {
        transition: fill 0.5s, color 0.5s;
      }
    `};
    ${({ disabled }) => disabled && `pointer-events: none`};
    ${Spin} {
      margin-right: 5px;
    }
  }
`;

const StyledLink = styled.a<{
  disabled?: boolean;
}>`
  ${({ disabled }) => disabled && `pointer-events: none`};
`;

export default styled(Button)``;
